import React, { useState } from 'react';
import { Loader2, FileDown, FileText, Presentation, Sparkles, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { downloadAdminAuditReportByAnalysis, api } from '../../lib/api';

/**
 * BrandedReportButton — admin/editor-only colored CTA that downloads a branded
 * audit report for a SPECIFIC analysis (PDF or PPTX, partial or full tier).
 *
 * Use this on per-analysis surfaces (Dashboard history cards, analysis result
 * pages) so users can drill into a specific audit even when one user has
 * analyzed many different profiles.
 *
 * Props:
 *   - `analysisId` (string, required)
 *   - `label` (string) — used in the downloaded file name
 *   - `size` ('sm' | 'default') — Shadcn Button size
 *   - `compact` (bool) — if true renders as an icon-only button with tooltip
 *
 * Visual: vivid primary-tinted button with the Sparkles icon to set it apart
 * from the muted "Open" / "Delete" secondary actions on the same card.
 */
export default function BrandedReportButton({
  analysisId,
  label = 'audit',
  size = 'sm',
  compact = false,
  disabled = false,
  isLegacy = false,
  onConverted,
}) {
  const [busy, setBusy] = useState(false);

  async function trigger(tier, format) {
    if (busy || !analysisId) return;
    setBusy(true);
    try {
      const niceTier = tier === 'partial' ? 'preview' : 'full report';
      const niceFmt = format === 'pptx' ? 'PPTX' : 'PDF';
      toast.info(`Generating ${niceTier} (${niceFmt})…`);
      await downloadAdminAuditReportByAnalysis(analysisId, label, tier, format);
      toast.success(`${niceTier === 'preview' ? 'Preview' : 'Full report'} (${niceFmt}) downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  }

  async function convertToV2() {
    if (busy || !analysisId) return;
    if (!window.confirm(
      'Re-run the v2 pipeline on this legacy audit? '
      + 'Uses the stored profile data — usually takes 1–3 minutes.',
    )) return;
    setBusy(true);
    const t = toast.loading('Converting to v2… (may take up to 3 min)');
    try {
      const { data } = await api.post(`/admin/analyses/${analysisId}/convert-to-v2`, {});
      toast.success('Converted to v2', {
        id: t,
        description: `New overall score: ${data?.overall_score ?? '—'}`,
      });
      onConverted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Conversion failed', { id: t });
    } finally {
      setBusy(false);
    }
  }

  async function regenerate(kind) {
    if (busy || !analysisId) return;
    setBusy(true);
    const label = kind === 'peer_benchmark'
      ? 'peer benchmark'
      : kind === 'pillars'
        ? 'profile pillars'
        : kind === 'topics'
          ? 'trending topics'
          : 'all data';
    try {
      toast.info(`Regenerating ${label}…`);
      let path;
      if (kind === 'all') {
        path = `/admin/analyses/${analysisId}/refresh-all`;
      } else if (kind === 'peer_benchmark') {
        path = `/admin/analyses/${analysisId}/regenerate-peer-benchmark`;
      } else if (kind === 'topics') {
        path = `/admin/analyses/${analysisId}/regenerate-trending-topics`;
      } else {
        path = `/admin/analyses/${analysisId}/regenerate-pillars`;
      }
      const { data } = await api.post(path);
      if (kind === 'all') {
        const r = data?.results || {};
        const STRUCTURAL_SKIPS = new Set(['not_scraped_profile', 'legacy_no_llm_peer', 'no_role_industry']);
        const HUMAN_REASON = {
          'not_scraped_profile': 'skipped (PDF-imported)',
          'legacy_no_llm_peer': 'skipped (legacy audit)',
          'no_role_industry': 'skipped (no role/industry)',
          'no_news_returned': 'no news matched — retry later',
          'perplexity_timeout': 'timed out — retry later',
          'already_refreshing': 'already running',
          'queued': 'queued',
        };
        const asHuman = (step) => {
          if (!step) return 'error';
          if (step.ok && step.status) return step.status;
          if (step.ok) return step.count ? `${step.count} loaded` : 'refreshed';
          return HUMAN_REASON[step.reason] || step.reason || 'error';
        };
        const glyph = (step) => step?.ok
          ? '\u2713'
          : (STRUCTURAL_SKIPS.has(step?.reason) ? '\u2013' : '\u2717');
        const bits = [
          `${glyph(r.pillars)} pillars${r.pillars?.ok ? '' : ` (${asHuman(r.pillars)})`}`,
          `${glyph(r.peer_benchmark)} peer benchmark${r.peer_benchmark?.ok ? '' : ` (${asHuman(r.peer_benchmark)})`}`,
          `${glyph(r.topics)} topics${r.topics?.ok ? ` (${r.topics.count} real)` : ` (${asHuman(r.topics)})`}`,
          `${glyph(r.leaders)} peers${r.leaders?.ok ? ` (${r.leaders.count} voices)` : ` (${asHuman(r.leaders)})`}`,
          `${glyph(r.research)} research (${asHuman(r.research)})`,
        ];
        const realFailure =
          (r.pillars && !r.pillars.ok && !STRUCTURAL_SKIPS.has(r.pillars.reason)) ||
          (r.peer_benchmark && !r.peer_benchmark.ok && !STRUCTURAL_SKIPS.has(r.peer_benchmark.reason)) ||
          (r.topics && !r.topics.ok && !STRUCTURAL_SKIPS.has(r.topics.reason)) ||
          (r.leaders && !r.leaders.ok && !STRUCTURAL_SKIPS.has(r.leaders.reason)) ||
          (r.research && !r.research.ok);
        const tId = realFailure
          ? toast.warning('Refresh finished with warnings', { description: bits.join(' \u00b7 '), duration: 10000 })
          : toast.success('Refresh complete — polling research…', { description: bits.join(' \u00b7 '), duration: 8000 });

        // Poll research_status until the async background task finishes.
        // Stops on: ready, error, or 90s ceiling.
        if (r.research?.ok) {
          const started = Date.now();
          const poll = async () => {
            if (Date.now() - started > 90_000) {
              toast.warning('Research still running after 90s — check back in a minute.', { id: tId });
              return;
            }
            try {
              const { data: fresh } = await api.get(`/analyze/${analysisId}`);
              const status = fresh?.research_status;
              if (status === 'ready') {
                toast.success('Research refresh complete', {
                  id: tId,
                  description: `${bits.join(' \u00b7 ')}${'\n'}Latest citations pool ready.`,
                });
                return;
              }
              if (status === 'error') {
                toast.error('Research failed', {
                  id: tId,
                  description: fresh?.research_error?.slice(0, 200) || 'Unknown error',
                });
                return;
              }
            } catch { /* ignore poll errors */ }
            setTimeout(poll, 3000);
          };
          setTimeout(poll, 3000);
        }
      } else {
        toast.success(`${label} refreshed — will appear in the next branded report.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || `Regenerate ${label} failed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size={size}
          disabled={disabled || busy}
          className={[
            'gap-1.5 font-semibold',
            'bg-primary text-primary-foreground hover:bg-primary/90',
            'shadow-[0_8px_22px_-10px_hsl(var(--primary)/0.55)]',
            'border border-primary/30',
            compact ? 'h-8 w-8 px-0' : '',
          ].join(' ')}
          aria-label="Download branded audit report"
          data-testid={`branded-report-trigger-${analysisId}`}
        >
          {busy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Sparkles size={14} />
          )}
          {!compact && <span>Branded report</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72"
        data-testid={`branded-report-menu-${analysisId}`}
      >
        <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-muted-foreground">
          BRANDED AUDIT REPORT
        </DropdownMenuLabel>

        <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-primary pt-2 pb-1">
          CLIENT PREVIEW (REDACTED)
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => trigger('partial', 'pdf')}
          data-testid={`branded-report-partial-pdf-${analysisId}`}
        >
          <FileText size={14} className="mr-2 text-primary" />
          <div className="flex flex-col flex-1">
            <span>Partial preview · PDF</span>
            <span className="text-[10px] text-muted-foreground">
              Scores + gaps. Suggestions redacted.
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => trigger('partial', 'pptx')}
          data-testid={`branded-report-partial-pptx-${analysisId}`}
        >
          <Presentation size={14} className="mr-2 text-primary" />
          <div className="flex flex-col flex-1">
            <span>Partial preview · PPTX</span>
            <span className="text-[10px] text-muted-foreground">
              Editable PowerPoint deck (16:9).
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-primary pt-1 pb-1">
          FULL REPORT
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => trigger('full', 'pdf')}
          data-testid={`branded-report-full-pdf-${analysisId}`}
        >
          <FileDown size={14} className="mr-2 text-primary" />
          <div className="flex flex-col flex-1">
            <span>Full report · PDF</span>
            <span className="text-[10px] text-muted-foreground">
              Everything: rewrites, peers, topics, benchmark, pillars.
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => trigger('full', 'pptx')}
          data-testid={`branded-report-full-pptx-${analysisId}`}
        >
          <Presentation size={14} className="mr-2 text-primary" />
          <div className="flex flex-col flex-1">
            <span>Full report · PPTX</span>
            <span className="text-[10px] text-muted-foreground">
              Editable widescreen slides.
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-muted-foreground pt-1 pb-1">
          REGENERATE DATA
        </DropdownMenuLabel>
        {isLegacy && (
          <DropdownMenuItem
            onClick={convertToV2}
            className="focus:bg-amber-500/10"
            data-testid={`branded-report-convert-v2-${analysisId}`}
          >
            <Zap size={14} className="mr-2 text-amber-600" />
            <div className="flex flex-col flex-1">
              <span className="font-semibold text-amber-700 dark:text-amber-400">
                Convert to v2
              </span>
              <span className="text-[10px] text-muted-foreground">
                Re-runs the v2 pipeline · clears the Legacy chip.
              </span>
            </div>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => regenerate('all')}
          data-testid={`branded-report-regen-all-${analysisId}`}
        >
          <Sparkles size={14} className="mr-2 text-primary" />
          <div className="flex flex-col flex-1">
            <span className="font-semibold">Refresh all data</span>
            <span className="text-[10px] text-muted-foreground">
              Pillars + peer benchmark + research (peers &amp; topics).
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => regenerate('topics')}
          data-testid={`branded-report-regen-topics-${analysisId}`}
        >
          <RefreshCw size={14} className="mr-2 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <span>Refresh trending topics</span>
            <span className="text-[10px] text-muted-foreground">
              Real Perplexity news · replaces LLM-invented titles.
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => regenerate('peer_benchmark')}
          data-testid={`branded-report-regen-peer-${analysisId}`}
        >
          <RefreshCw size={14} className="mr-2 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <span>Refresh peer benchmark</span>
            <span className="text-[10px] text-muted-foreground">
              Rebuilds the peer comparison block.
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => regenerate('pillars')}
          data-testid={`branded-report-regen-pillars-${analysisId}`}
        >
          <RefreshCw size={14} className="mr-2 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <span>Refresh profile pillars</span>
            <span className="text-[10px] text-muted-foreground">
              Rescores Discoverability / Positioning / Credibility.
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
